import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  AtsDeleteApplicationPhaseDocument,
  AtsDeleteApplicationPhaseMutation,
  AtsGetHiringPipelinePhasesDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DeleteApplicationPhaseSkeleton } from './DeleteApplicationPhaseSkeleton'

interface FormValues {
  applicationPhaseId: string
}

interface Props {
  id: string
  jobId: string
  onClose: () => void
}

export const DeleteApplicationPhaseModal = ({ onClose, id, jobId }: Props) => {
  const [deleteAtsApplicationPhase, { loading }] = useMutation(AtsDeleteApplicationPhaseDocument, {
    extractorFn: (data: AtsDeleteApplicationPhaseMutation) =>
      data.atsMutations.deleteAtsApplicationPhase,
  })

  const { loading: loadingPhases, data: phasesData } = useQuery(
    AtsGetHiringPipelinePhasesDocument,
    {
      variables: { jobId },
    }
  )

  const form = useForm<FormValues>({
    defaultValues: {
      applicationPhaseId: '0',
    },

    validate: (data) => {
      const errors: FormErrors<FormValues> = {}

      if (data.applicationPhaseId === '0') {
        errors.applicationPhaseId = i18n.t('common.option_selected.zero')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await deleteAtsApplicationPhase({
        variables: {
          id,
          alternativeApplicationPhaseId: data.applicationPhaseId,
        },
        update(cache) {
          cache.evict({ id: cache.identify({ id, __typename: 'AtsApplicationPhase' }) })
        },
      })

      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.removed_successfully'),
        })
        onClose()
      }
      return handled
    },
  })

  const applicationPhases = phasesData?.ats.applicationPhasesConnection.nodes || []

  const options = applicationPhases
    .filter((phase) => phase.id !== id)
    .map((phase) => {
      return {
        label: phase.name,
        value: phase.id,
      }
    })

  return (
    <Modal
      title={i18n.t(
        'ats.job_posting_settings.hiring_pipeline.delete_phase_with_applications_modal.title'
      )}
      description={i18n.t(
        'ats.job_posting_settings.hiring_pipeline.delete_phase_with_applications_modal.description'
      )}
      illustration="automations"
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={() => onClose()}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" submit loading={loading}>
            {i18n.t('common.delete')}
          </Button>
        </Box>
      }
    >
      {loadingPhases || !applicationPhases ? (
        <DeleteApplicationPhaseSkeleton />
      ) : (
        <Select
          none={i18n.t('common.select')}
          label={i18n.t('ats.job_posting_settings.hiring_pipeline.phase_input_label')}
          options={options}
          value={String(form.values.applicationPhaseId)}
          error={form.errors.applicationPhaseId}
          onChange={(value) => form.setFieldValue('applicationPhaseId', String(value))}
          mandatory
        />
      )}
    </Modal>
  )
}
